import { getRoot } from 'mobx-state-tree';
import { LUSHA_INTEGRATIONS } from '../../../components/SaveContact/constants';
import { BUTTON_TYPES } from '../../../components/ShowContactByDataPoint/constants';
import { CONTACT_STATES } from '../../../utils/constants';
import { buildContactsDataPointDataForShow } from '../../../utils/showHelper';
import { BULK_FILTERS_KEY } from '../../../views/BulkEnrich/constants';
import { INDUSTRY_TO_DEMO_PROFILE } from '../../User/Views';
import { ACQUIRE_STATUS, DEFAULT_LIST_ID } from '../constants';
export const CONTACT_TYPES = {
    WISHLIST_CONTACT: 'WISHLIST_CONTACT',
    SHOWN_CONTACT: 'SHOWN_CONTACT',
};
const Views = self => ({
    get totalDataCollected() {
        return self.totalEmailsCollected + self.totalPhonesCollected;
    },
    get isWithoutData() {
        const contactNoData = self.totalDataCollected === 0 && self.isAcquired;
        if (!self.isWishlistContact) {
            return contactNoData;
        }
        return self.wishlistContactDetails.length === 0;
    },
    get isNotAllDataPointsSaved() {
        var _a;
        if (!((_a = self.savedDatapoints) === null || _a === void 0 ? void 0 : _a.length)) {
            return false;
        }
        const revealedDatapoints = [self.phone1, self.phone2, self.email1, self.email2]
            .map(dataPoint => (!dataPoint || !dataPoint.isRevealed ? null : dataPoint.id))
            .filter(Boolean);
        return revealedDatapoints.some(datapoint => !self.savedDatapoints.includes(datapoint));
    },
    get isAcquired() {
        return self.isRevealed || self.acquireStatus === ACQUIRE_STATUS.complete;
    },
    get totalEmailsCollected() {
        return !!self.email1.value + !!self.email2.value;
    },
    get totalPhonesCollected() {
        return !!self.phone1.value + !!self.phone2.value;
    },
    get isEmpty() {
        return (!self.fullName ||
            (!self.email1.value && !self.email2.value && !self.phone1.value && !self.phone2.value && !self.company.hasCompany));
    },
    get isWishlistContact() {
        var _a, _b;
        const isEmpty = !self.fullName || (!self.email1.value && !self.email2.value && !self.phone1.value && !self.phone2.value);
        return ((!!self.wishlistData && isEmpty) ||
            (self.isPartialProfile && !!((_b = (_a = self.wishlistData) === null || _a === void 0 ? void 0 : _a.lists) === null || _b === void 0 ? void 0 : _b.length) && !self.isRevealed));
    },
    get isMissingDetails() {
        return !self.fullName || (!self.email1.value && !self.email2.value && !self.phone1.value && !self.phone2.value);
    },
    get isMissingFullName() {
        return !self.fullName;
    },
    get hasChanges() {
        return self.state === CONTACT_STATES.REFRESHED;
    },
    get emails() {
        const emails = [];
        if (self.email1.value) {
            emails.push(self.email1);
        }
        if (self.email2.value) {
            emails.push(self.email2);
        }
        return emails;
    },
    get phones() {
        const phones = [];
        if (self.phone1.value) {
            phones.push(self.phone1);
        }
        if (self.phone2.value) {
            phones.push(self.phone2);
        }
        return phones;
    },
    get hasWorkEmail() {
        return (self.email1.value && self.email1.type === 'Work') || (self.email2.value && self.email2.type === 'Work');
    },
    get hasDirectEmail() {
        return (self.email1.value && self.email1.type === 'Direct') || (self.email2.value && self.email2.type === 'Direct');
    },
    get isContactSaved() {
        return self.savedCrmIntegrations.length > 0 || self.isSavedToList;
    },
    get contactResults() {
        var _a;
        const contact = JSON.parse(self.searchStringResult);
        if (self.isWishlistContact) {
            contact.contact = self.wishlistData;
            contact.contactId = self.wishlistData.id;
        }
        if (!contact.contact) {
            contact.contact = {};
        }
        const company = ((_a = self === null || self === void 0 ? void 0 : self.company) === null || _a === void 0 ? void 0 : _a.companyResultString) && JSON.parse(self.company.companyResultString);
        const searchVersion = '2.0';
        const apiVersion = 3;
        const contactResults = Object.assign(Object.assign({ company, contactType: self.contactType }, contact), { searchVersion,
            apiVersion });
        // Fallback for jobTitle & name for v1/v2 search after show descrepencies.
        contactResults.job_title = contactResults.job_title || contactResults.jobTitle;
        contactResults.jobTitles = contactResults.jobTitles || [contactResults.job_title];
        if (!contactResults.name) {
            contactResults.name = contactResults.fullName || self.fullName;
        }
        return contactResults;
    },
    get savedCrmIntegrations() {
        return Array.from(self.integrationsData.keys());
    },
    get savedIntegrations() {
        return self.isSavedToList ? self.savedCrmIntegrations.concat([LUSHA_INTEGRATIONS.Lusha]) : self.savedCrmIntegrations;
    },
    get isSavedToList() {
        return self.contactList ? self.contactList !== DEFAULT_LIST_ID : false;
    },
    getIntegrationUrl(integrationKey) {
        return self.integrationsData.get(integrationKey).link;
    },
    shouldDisplayIntegrationIndication(integrationKey) {
        var _a;
        const { outdatedSince, lastUpdated } = (_a = self.integrationsData.get(integrationKey)) !== null && _a !== void 0 ? _a : {};
        return (lastUpdated && lastUpdated < outdatedSince) || outdatedSince;
    },
    get hasAnyTags() {
        return self.tags.length > 0;
    },
    get isAddingData() {
        return self.addContactData.contactKeyBeingAdded !== null;
    },
    get isOnboardingProfile() {
        const onboardingProfiles = Object.values(INDUSTRY_TO_DEMO_PROFILE);
        const isOnboardingProfile = onboardingProfiles.some(profile => self.socialLink.includes(profile));
        return self.isOnboarding || isOnboardingProfile;
    },
    get canAddData() {
        if (self.isAddingData) {
            return false;
        }
        if (self.isWishlistContact) {
            return self.wishlistData.emails.length === 0 || self.wishlistData.phones.length === 0;
        }
        if (self.isOnboardingProfile) {
            return false;
        }
        if (!self.isRevealed) {
            return false;
        }
        return self.emails.length < 2 || self.phones.length < 2;
    },
    get wishlistContactDetails() {
        var _a, _b, _c, _d;
        const details = [];
        ((_a = self === null || self === void 0 ? void 0 : self.wishlistData) === null || _a === void 0 ? void 0 : _a.email1) && details.push({ label: 'Email', key: 'email1', data: self.wishlistData.email1 });
        ((_b = self === null || self === void 0 ? void 0 : self.wishlistData) === null || _b === void 0 ? void 0 : _b.email2) && details.push({ label: 'Email', key: 'email2', data: self.wishlistData.email2 });
        ((_c = self === null || self === void 0 ? void 0 : self.wishlistData) === null || _c === void 0 ? void 0 : _c.phone1) && details.push({ label: 'Phone', key: 'phone1', data: self.wishlistData.phone1 });
        ((_d = self === null || self === void 0 ? void 0 : self.wishlistData) === null || _d === void 0 ? void 0 : _d.phone2) && details.push({ label: 'Phone', key: 'phone2', data: self.wishlistData.phone2 });
        return details;
    },
    get hasOnlyDncPhones() {
        const hasEmails = self.emails.length > 0;
        const hasMultiplePhones = self.phones.length === 2;
        // TODO: the !hasMultiplePhones check is very bad :(
        // TODO: in the store refactor we need to initialize the second phone object only when there is a value.
        return !hasEmails && self.phone1.isDncPhone && (self.phone2.isDncPhone || !hasMultiplePhones);
    },
    get hasDncPhone() {
        return self.phone1.isDncPhone || self.phone2.isDncPhone;
    },
    get isNotEligibleToBeShown() {
        return self.isBlockedForShow || self.hasOnlyDncPhones;
    },
    get isRevealedByAnotherUser() {
        return false;
    },
    get isBulkCheckboxDisabled() {
        const { prospecting } = getRoot(self);
        if (self.isWishlistContact) {
            return false;
        }
        const isWishlistNoDataFilter = prospecting.activeBulkFilters.includes(BULK_FILTERS_KEY.wishlistNoData);
        if (isWishlistNoDataFilter) {
            return true;
        }
        const isWishlistFilter = prospecting.activeBulkFilters.includes(BULK_FILTERS_KEY.wishlist);
        if (isWishlistFilter) {
            return false;
        }
        return !!(self.isBlockedForShow || self.hasOnlyDncPhones || self.isWithoutData);
    },
    get contactType() {
        if (self.isWishlistContact) {
            return CONTACT_TYPES.WISHLIST_CONTACT;
        }
        if (self.isRevealed) {
            return CONTACT_TYPES.SHOWN_CONTACT;
        }
        return null;
    },
    get dataPointToShow() {
        const hasEmailToShow = self.contactHasMaskedEmails;
        const hasPhoneToShow = self.contactHasMaskedPhones;
        if (hasEmailToShow && hasPhoneToShow) {
            return BUTTON_TYPES.EMAIL_AND_PHONE;
        }
        if (hasEmailToShow) {
            return BUTTON_TYPES.EMAIL;
        }
        if (hasPhoneToShow) {
            return BUTTON_TYPES.PHONE;
        }
        return BUTTON_TYPES.NONE;
    },
    get contactHasMaskedEmails() {
        return self.emails.some(email => email.status === 'masked');
    },
    get contactHasMaskedPhones() {
        return self.phones.some(phone => phone.status === 'masked');
    },
    get contactHasShownEmails() {
        return self.emails.some(email => email.status === 'shown');
    },
    get contactHasShownPhones() {
        return self.phones.some(phone => phone.status === 'shown');
    },
    get hasOnlyMaskedData() {
        const hasData = self.totalDataCollected > 0;
        const allEmailsMasked = self.emails.length === 0 || self.emails.every(email => email.status === 'masked');
        const allPhonesMasked = self.phones.length === 0 || self.phones.every(phone => phone.status === 'masked');
        return hasData && allEmailsMasked && allPhonesMasked;
    },
    get dataPointIdsToShow() {
        const selectedDataPoints = self.isOnboarding ? BUTTON_TYPES.EMAIL_AND_PHONE : self.selectedDataPointType;
        const contacts = [
            {
                id: self.id,
                maskedContactId: self.maskedContactId,
                selectedDataPointType: selectedDataPoints,
                emails: self.emails,
                phones: self.phones,
            },
        ];
        return buildContactsDataPointDataForShow(contacts, selectedDataPoints);
    },
    get hasEmailDataPoint() {
        var _a, _b;
        return !!((_a = self === null || self === void 0 ? void 0 : self.email1) === null || _a === void 0 ? void 0 : _a.value) || !!((_b = self === null || self === void 0 ? void 0 : self.email2) === null || _b === void 0 ? void 0 : _b.value);
    },
    get hasPhoneDataPoint() {
        var _a, _b;
        return !!((_a = self === null || self === void 0 ? void 0 : self.phone1) === null || _a === void 0 ? void 0 : _a.value) || !!((_b = self === null || self === void 0 ? void 0 : self.phone2) === null || _b === void 0 ? void 0 : _b.value);
    },
    get getCompany() {
        const { storeCompanyKey } = self;
        if (!storeCompanyKey) {
            return self.company;
        }
        const { companies } = getRoot(self);
        const storeCompany = companies === null || companies === void 0 ? void 0 : companies.get(storeCompanyKey);
        if (!storeCompany) {
            return {};
        }
        return storeCompany;
    },
});
export default Views;
